import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import { ECustomerSearchTypes } from 'v2/interfaces/reports/partnerCommissions';
import type {
  IPartnerCommission,
  IPartnerCommissionFilter,
  IPartnerCommissionsDetails
} from 'v2/interfaces/reports/partnerCommissions';

import apiService from '../api';
import type { ApiService } from '../api';

const today = formatDate(new Date(), 'dd/MM/yyyy');

export const initialFilter: IPartnerCommissionFilter = {
  commissioned: '',
  commissioned_type: ECustomerSearchTypes.EMAIL,
  date: [today, today]
};

const initialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10
};

class PartnerCommissionsService {
  private filters$ = new Rx.BehaviorSubject<IPartnerCommissionFilter>(initialFilter);

  private loadingCommissions$ = new Rx.BehaviorSubject<boolean>(true);
  private loadingPartnerCommissionsDetails$ = new Rx.BehaviorSubject<boolean>(true);
  private commissions$ = new Rx.BehaviorSubject<IPartnerCommission[]>([]);
  private partnerCommissionsDetails$ = new Rx.BehaviorSubject<IPartnerCommissionsDetails>(
    {} as IPartnerCommissionsDetails
  );

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(initialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private totalGain$ = new Rx.BehaviorSubject<number>(0);

  constructor(private apiService: ApiService) {}

  public setFilter(filter: IPartnerCommissionFilter): void {
    this.loadingCommissions$.next(true);

    this.filters$.pipe(take(1)).subscribe((currentFilter: IPartnerCommissionFilter) => {
      this.filters$.next({ ...currentFilter, ...filter });
    });
  }

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public getTotalGain = () => this.totalGain$;

  public getFilters = (): Rx.Observable<IPartnerCommissionFilter> => this.filters$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public isLoading = () => this.loadingCommissions$;

  public getCommissions = (): Rx.Observable<IPartnerCommission[]> => this.commissions$;

  public formatFilter = (filter: IPartnerCommissionFilter) => {
    const newFilter: Partial<IPartnerCommissionFilter> = {};
    newFilter.created_at = filter?.date[0];
    newFilter.created_at_final = filter?.date[1];
    (newFilter as any)[filter.commissioned_type] = filter.commissioned;

    return newFilter;
  };

  public loadCommissions() {
    return this.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter => this.resetPage().pipe(map(params => ({ ...filter, ...params })))),
      debounceTime(10),
      switchMap(filter => {
        this.loadingCommissions$.next(true);
        return this.apiService.get('reports/sales/commissions/partners', filter);
      }),
      tap(response => {
        this.commissions$.next(response.data);
        this.totalGain$.next(response.total_gain);
        this.paginationResponse$.next(response.pagination);
        this.totalItems$.next(response.pagination.total_items);
        this.loadingCommissions$.next(false);
      })
    );
  }

  public export() {
    return this.apiService.get('reports/sales/commissions/partners', {
      ...this.formatFilter(this.filters$.value),
      export: true
    });
  }

  public cleanFilter() {
    this.filters$.next(initialFilter);
  }

  public requestPartnerCommissionsDetails(commissionedId: number) {
    this.loadingPartnerCommissionsDetails$.next(true);
    return this.apiService
      .get(`reports/sales/commissions/partners/${commissionedId}`, {
        ...this.formatFilter(this.filters$.value),
        export: true
      })
      .pipe(
        map(response => response.data),
        tap(data => {
          this.partnerCommissionsDetails$.next(data);
          this.loadingPartnerCommissionsDetails$.next(false);
        })
      );
  }

  public getPartnerCommissionsDetails() {
    return this.partnerCommissionsDetails$;
  }

  public getLoadingPartnerCommissionsDetails() {
    return this.loadingPartnerCommissionsDetails$;
  }
}

export const partnerCommissionsService = new PartnerCommissionsService(apiService);
